import axios from 'axios'
import { Message } from 'element-ui'

const api = axios.create({
  baseURL: '/api',
  timeout: 30000,
})

// 报修接口双鉴权：优先 Web 后台登录令牌（ems_web_token），未登录后台时回退小程序令牌（ems_mp_admin_token）。
// 与盘点（api/inventory.js）保持同一套写法：用请求拦截器在每次调用时动态取值，
// 后台登录后无需刷新页面即可访问报修页。注意：utils/request 的通用实例只带 Web Token，用它访问会 403。
api.interceptors.request.use(config => {
  const webToken = localStorage.getItem('ems_web_token')
  const mpToken = localStorage.getItem('ems_mp_admin_token')
  const token = webToken || mpToken
  if (token) {
    config.headers['Authorization'] = 'Token ' + token
  } else {
    delete config.headers['Authorization']
  }
  return config
})

api.interceptors.response.use(
  response => response,
  error => {
    const status = error.response && error.response.status
    if (!status) {
      // 网络不通：请求根本没有 HTTP 响应（断网 / 超时 / DNS 失败 / 跨域被拦）
      Message.closeAll()
      Message.warning('网络无法连接后端，请检查网络或稍后重试')
    } else if (status >= 500) {
      // 服务器内部错误，与网络无关，不应误报成"未连接"
      Message.closeAll()
      Message.error('服务器异常（HTTP 500），请稍后重试或联系管理员')
    } else if (status === 403) {
      Message.error('无权限：请先登录后台账号，或在「设置令牌」中粘贴小程序后勤/管理员令牌')
    }
    return Promise.reject(error)
  }
)

export default {
  // 工单列表（分页）。筛选参数：status / department / keyword / is_emergency /
  // start_date / end_date / page / page_size（另有 accept_by 按接单人筛选，后端未支持时会忽略）
  listOrders(params) {
    return api.get('/mp/repair/orders/', { params })
  },
  // 接单池（主页大屏用）：仅 PENDING 工单，后端按「急救置顶 > 回退加急置顶 > 先来先接」排序。
  // 返回分页结构 { count, results }，字段同 listOrders。
  getPool(params) {
    return api.get('/mp/repair/pool/', { params })
  },
  // 工单详情：全字段 + 时间线字段 + 附件 + 配件明细
  getOrderDetail(orderNo) {
    return api.get(`/mp/repair/orders/${encodeURIComponent(orderNo)}/`)
  },
  // 下载维修记录单 PDF（blob，带鉴权头；写法与盘点 downloadReport 一致）
  getReport(orderNo) {
    return api.get(`/mp/repair/orders/${encodeURIComponent(orderNo)}/report/`, {
      responseType: 'blob',
      timeout: 60000,
    })
  },
  // 派单（仅 PENDING 工单可用）：{ target_openid }
  dispatch(orderNo, data) {
    return api.post(`/mp/repair/orders/${encodeURIComponent(orderNo)}/dispatch/`, data)
  },
  // 后勤人员列表：[{ openid, name, dept, role }]，派单选人用
  listStaff() {
    return api.get('/mp/repair/staff/')
  },
  // 科室花费统计汇总：后端聚合（前端不拉全量工单）。
  // 参数：start_date / end_date / status / department（模糊）/ keyword
  // 返回 { rows: [{ department, total, doing, done, canceled, emergency, cost }], summary: {...} }
  getDeptStats(params) {
    return api.get('/mp/repair/stats/by-dept/', { params })
  },
  // 某科室的工单明细（后端分页，LenientPageNumberPagination 越界回退末页）。
  // 参数：department（必填）/ start_date / end_date / status / keyword / page / page_size
  getDeptStatsDetail(params) {
    return api.get('/mp/repair/stats/by-dept/detail/', { params })
  },
  // 科室花费统计导出：后端直接生成 xlsx，返回 blob（前端只负责落盘下载）。
  // 导出耗时长，单独放宽超时到 120s。
  exportDeptStats(params) {
    return api.get('/mp/repair/stats/by-dept/export/', {
      params,
      responseType: 'blob',
      timeout: 120000,
    })
  },
}
